import { qs, prefersReducedMotion } from '../core/dom.js';

// Hash / in-page anchor navigation.
// Reload always restores the last scroll position (even if the URL still has a
// #hash from an earlier nav click). Hash scroll only runs on fresh navigation.

const SCROLL_KEY_PREFIX = 'lpr:scroll:';

export function scrollStorageKey() {
	return `${SCROLL_KEY_PREFIX}${window.location.pathname}${window.location.search}`;
}

function isReload() {
	return performance.getEntriesByType('navigation')[0]?.type === 'reload';
}

function scrollBehavior(smooth) {
	if (prefersReducedMotion() || !smooth) return 'auto';
	return 'smooth';
}

function scrollOffset() {
	const header = qs('.site-head');
	return (header?.offsetHeight ?? 80) + 8;
}

export function scrollToHash(hash, { smooth = false } = {}) {
	const id = decodeURIComponent(String(hash || '').replace(/^#/, ''));
	const top = id === '' || id === 'top'
		? 0
		: (() => {
			const el = document.getElementById(id);
			if (!el) return null;
			return Math.max(0, el.getBoundingClientRect().top + window.scrollY - scrollOffset());
		})();

	if (top === null) return false;

	window.scrollTo({ top, behavior: scrollBehavior(smooth) });
	return true;
}

function applyLocationHash({ smooth = false } = {}) {
	const { hash } = window.location;
	if (!hash) return;
	scrollToHash(hash, { smooth });
}

function restoreSavedScroll() {
	const raw = sessionStorage.getItem(scrollStorageKey());
	if (raw === null) return false;

	const y = Number(raw);
	if (!Number.isFinite(y) || y < 0) return false;

	window.scrollTo({ top: y, left: 0, behavior: 'auto' });
	return true;
}

function reinforceScrollRestore() {
	const apply = () => {
		restoreSavedScroll();
	};
	apply();
	requestAnimationFrame(apply);
	window.addEventListener('load', apply, { once: true });
}

export function initHashScroll() {
	window.addEventListener('pagehide', () => {
		sessionStorage.setItem(scrollStorageKey(), String(window.scrollY));
	});

	document.addEventListener('click', (event) => {
		const link = event.target.closest('a[href*="#"]');
		if (!link || link.target === '_blank' || link.hasAttribute('download')) return;

		const url = new URL(link.href, window.location.href);
		if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;

		const { hash } = url;
		if (!hash) return;

		event.preventDefault();
		if (!scrollToHash(hash, { smooth: true })) return;

		if (hash !== window.location.hash) {
			history.pushState(null, '', hash);
		}
	});

	window.addEventListener('hashchange', () => {
		if (isReload()) return;
		applyLocationHash({ smooth: false });
	});

	if (isReload()) {
		if ('scrollRestoration' in history) {
			history.scrollRestoration = 'manual';
		}
		reinforceScrollRestore();
		return;
	}

	if (window.location.hash) {
		if ('scrollRestoration' in history) {
			history.scrollRestoration = 'manual';
		}
		applyLocationHash({ smooth: false });
		requestAnimationFrame(() => applyLocationHash({ smooth: false }));
		window.addEventListener('load', () => applyLocationHash({ smooth: false }), { once: true });
		return;
	}

	if ('scrollRestoration' in history) {
		history.scrollRestoration = 'auto';
	}
}
